import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import SharedNav from './SharedNav';
import './PlaybookPage.css';

const TYPES = ['weekly', 'monthly', 'adhoc'];
const TYPE_LABELS = { weekly: 'Weekly Tasks', monthly: 'Monthly Tasks', adhoc: 'Adhoc Tasks' };
const TYPE_COLORS = { weekly: '#2563eb', monthly: '#7c3aed', adhoc: '#0891b2' };

const CARD_COLORS = [
  { label: 'Navy',   value: '#1e2d5a' },
  { label: 'Blue',   value: '#2563eb' },
  { label: 'Purple', value: '#7c3aed' },
  { label: 'Teal',   value: '#0891b2' },
  { label: 'Green',  value: '#16a34a' },
  { label: 'Amber',  value: '#d97706' },
  { label: 'Red',    value: '#dc2626' },
];

function emptyCardForm() {
  return { title: '', body: '', color: '#2563eb' };
}

export default function PlaybookPage({ user, onLogout }) {
  const navigate = useNavigate();
  const isAdmin = ['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase());

  // ── SOP Cards state ─────────────────────────────────────────────────────
  const [cards, setCards] = useState([]);
  const [cardForm, setCardForm] = useState(emptyCardForm());
  const [editingCard, setEditingCard] = useState(null); // card object or null
  const [showCardModal, setShowCardModal] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const editorRef = useRef(null);

  // ── Process flow state ──────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('indeed');
  const [content, setContent] = useState({ indeed: '', fadv: '' });
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Task tracker state ──────────────────────────────────────────────────
  const [liveCompanies, setLiveCompanies] = useState([]);
  const [tasks, setTasks] = useState({ weekly: [], monthly: [], adhoc: [] });
  const [completions, setCompletions] = useState({});
  const [toggling, setToggling] = useState({});
  const [addingType, setAddingType] = useState(null);
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskCompanyId, setNewTaskCompanyId] = useState('');

  // ── Loaders ─────────────────────────────────────────────────────────────
  const loadCards = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/sop-cards');
      setCards(data);
    } catch {}
  }, []);

  const loadContent = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/playbook/content');
      const map = { indeed: '', fadv: '' };
      data.forEach(r => { map[r.section] = r.content || ''; });
      setContent(map);
    } catch {}
  }, []);

  const loadLiveCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/checklist/live-companies');
      setLiveCompanies(data);
    } catch {}
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/checklist/tasks');
      const grouped = { weekly: [], monthly: [], adhoc: [] };
      data.forEach(t => { if (grouped[t.type]) grouped[t.type].push(t); });
      setTasks(grouped);
    } catch {}
  }, []);

  const loadCompletions = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/checklist/completions');
      const map = {};
      data.forEach(c => { map[`${c.task_id}-${c.company_id}`] = true; });
      setCompletions(map);
    } catch {}
  }, []);

  useEffect(() => {
    loadCards();
    loadContent();
    loadLiveCompanies();
    loadTasks();
    loadCompletions();
  }, [loadCards, loadContent, loadLiveCompanies, loadTasks, loadCompletions]);

  // ── Rich-text editor helpers ─────────────────────────────────────────────
  const execBold = () => {
    editorRef.current?.focus();
    document.execCommand('bold', false, null);
  };

  const execBullet = () => {
    editorRef.current?.focus();
    document.execCommand('insertUnorderedList', false, null);
  };

  const openNewCard = () => {
    setEditingCard(null);
    setCardForm(emptyCardForm());
    setShowCardModal(true);
  };

  const openEditCard = (card) => {
    setEditingCard(card);
    setCardForm({ title: card.title, body: card.body || '', color: card.color || '#2563eb' });
    setShowCardModal(true);
  };

  // Sync contenteditable body into cardForm after modal opens
  useEffect(() => {
    if (showCardModal && editorRef.current) {
      editorRef.current.innerHTML = cardForm.body || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCardModal, editingCard]);

  const saveCard = async () => {
    const body = editorRef.current?.innerHTML || '';
    const payload = { title: cardForm.title, body, color: cardForm.color };
    if (!payload.title.trim()) { alert('Title is required.'); return; }
    setSavingCard(true);
    try {
      if (editingCard) {
        const { data } = await axios.put(`/api/sop-cards/${editingCard.id}`, payload);
        setCards(prev => prev.map(c => c.id === editingCard.id ? data : c));
      } else {
        const { data } = await axios.post('/api/sop-cards', payload);
        setCards(prev => [...prev, data]);
      }
      setShowCardModal(false);
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.message));
    }
    setSavingCard(false);
  };

  const deleteCard = async (id) => {
    if (!window.confirm('Delete this card?')) return;
    try {
      await axios.delete(`/api/sop-cards/${id}`);
      setCards(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(cards);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setCards(reordered);
    try {
      await axios.put('/api/sop-cards/reorder', { order: reordered.map(c => c.id) });
    } catch {}
  };

  // ── Process flow actions ─────────────────────────────────────────────────
  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/playbook/content/${activeTab}`, { content: editValue });
      setContent(prev => ({ ...prev, [activeTab]: editValue }));
      setEditing(false);
    } catch {}
    setSaving(false);
  };

  // ── Task actions ─────────────────────────────────────────────────────────
  const addTask = async (type) => {
    if (!newTaskText.trim()) return;
    if (type === 'adhoc' && !newTaskCompanyId) return;
    try {
      const { data } = await axios.post('/api/checklist/tasks', {
        type,
        task_text: newTaskText.trim(),
        company_id: type === 'adhoc' ? newTaskCompanyId : null,
      });
      setTasks(prev => ({ ...prev, [type]: [...prev[type], data] }));
      setNewTaskText('');
      setNewTaskCompanyId('');
      setAddingType(null);
    } catch {}
  };

  const deleteTask = async (id, type) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`/api/checklist/tasks/${id}`);
      setTasks(prev => ({ ...prev, [type]: prev[type].filter(t => t.id !== id) }));
      setCompletions(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => { if (k.startsWith(`${id}-`)) delete next[k]; });
        return next;
      });
    } catch {}
  };

  const toggle = async (taskId, companyId, type) => {
    const key = `${taskId}-${companyId}`;
    if (toggling[key]) return;
    const wasChecked = !!completions[key];
    setCompletions(prev => ({ ...prev, [key]: !wasChecked }));
    setToggling(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post('/api/checklist/toggle', { task_id: taskId, company_id: companyId, type });
    } catch {
      setCompletions(prev => ({ ...prev, [key]: wasChecked }));
    }
    setToggling(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  // ── Table renderer ───────────────────────────────────────────────────────
  const renderTable = (type) => {
    const typeTasks = tasks[type];
    const isAdding = addingType === type;
    const color = TYPE_COLORS[type];

    return (
      <div className="pb-matrix-panel" key={type}>
        <div className="pb-matrix-panel-header" style={{ borderTopColor: color }}>
          <h3 style={{ color }}>{TYPE_LABELS[type]}</h3>
          <button
            className="pb-matrix-add-btn"
            style={{ background: color }}
            onClick={() => { setAddingType(type); setNewTaskText(''); setNewTaskCompanyId(''); }}
          >
            + Add
          </button>
        </div>

        <div className="pb-matrix-scroll mtn-scroll-x">
          <table className="pb-matrix-table">
            <thead>
              <tr>
                <th className="pb-task-th">Task</th>
                {liveCompanies.map(co => (
                  <th key={co.id} className="pb-co-th" title={co.name}>
                    {co.name.length > 9 ? co.name.slice(0, 8) + '…' : co.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {typeTasks.length === 0 && !isAdding && (
                <tr>
                  <td colSpan={liveCompanies.length + 1} className="pb-matrix-empty">
                    No tasks yet — click + Add
                  </td>
                </tr>
              )}
              {typeTasks.map(task => (
                <tr key={task.id}>
                  <td className="pb-task-td">
                    <button className="pb-del-task" onClick={() => deleteTask(task.id, type)} title="Delete task">✕</button>
                    <span className="pb-task-text" title={task.task_text}>{task.task_text}</span>
                  </td>
                  {liveCompanies.map(co => {
                    const applicable = type !== 'adhoc' || String(task.company_id) === String(co.id);
                    const key = `${task.id}-${co.id}`;
                    const checked = !!completions[key];
                    return (
                      <td key={co.id} className="pb-cell-td">
                        {applicable ? (
                          <button
                            className={`pb-check-btn ${checked ? 'checked' : ''}`}
                            onClick={() => toggle(task.id, co.id, type)}
                            title={checked ? 'Uncheck' : 'Check'}
                          >
                            {checked ? '✓' : ''}
                          </button>
                        ) : (
                          <span className="pb-na">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {isAdding && (
                <tr className="pb-add-row">
                  <td className="pb-add-td" colSpan={liveCompanies.length + 1}>
                    <div className="pb-add-form">
                      <input
                        className="pb-add-input"
                        autoFocus
                        placeholder="Task description…"
                        value={newTaskText}
                        onChange={e => setNewTaskText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addTask(type); if (e.key === 'Escape') setAddingType(null); }}
                      />
                      {type === 'adhoc' && (
                        <select
                          className="pb-add-select"
                          value={newTaskCompanyId}
                          onChange={e => setNewTaskCompanyId(e.target.value)}
                        >
                          <option value="">— Company —</option>
                          {liveCompanies.map(co => <option key={co.id} value={co.id}>{co.name}</option>)}
                        </select>
                      )}
                      <button className="pb-add-save" style={{ background: color }} onClick={() => addTask(type)}>Add</button>
                      <button className="pb-add-cancel" onClick={() => setAddingType(null)}>Cancel</button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const weekLabel = (() => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    return monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  })();

  const monthLabel = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="playbook-page">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="playbook-body">

        {/* ── Mission ── */}
        <section className="pb-section pb-mission-section">
          <div className="pb-mission-bar">
            <div className="pb-mission-accent" />
            <div>
              <div className="pb-mission-label">OUR MISSION</div>
              <div className="pb-mission-text">We find, equip, and empower the people who keep America moving — powered by AI.</div>
            </div>
          </div>
        </section>

        {/* ── Culture ── */}
        <section className="pb-section pb-culture-section">
          <h2 className="pb-culture-title">OUR CULTURE</h2>
          <div className="pb-culture-grid">
            <div className="pb-culture-card">
              <div className="pb-culture-num">01</div>
              <div className="pb-culture-name">Ownership</div>
              <div className="pb-culture-desc">Take responsibility and act like an owner. Your accounts, your outcomes.</div>
            </div>
            <div className="pb-culture-card">
              <div className="pb-culture-num">02</div>
              <div className="pb-culture-name">Hard Work</div>
              <div className="pb-culture-desc">We put in the effort others won't. Consistency and dedication drive results.</div>
            </div>
            <div className="pb-culture-card">
              <div className="pb-culture-num">03</div>
              <div className="pb-culture-name">Collaboration</div>
              <div className="pb-culture-desc">We win together. Share knowledge, support each other, and communicate openly.</div>
            </div>
            <div className="pb-culture-card">
              <div className="pb-culture-num">04</div>
              <div className="pb-culture-name">Problem Solving</div>
              <div className="pb-culture-desc">Every challenge is an opportunity. Think critically and find a way forward.</div>
            </div>
          </div>
        </section>

        {/* ── SOP Cards ── */}
        <section className="pb-section">
          <div className="pb-section-title">
            <h2>SOP Cards</h2>
            {isAdmin && (
              <button className="mtn-btn mtn-btn-edit" onClick={openNewCard}>+ Add Card</button>
            )}
          </div>

          {cards.length === 0 ? (
            <p className="pb-empty">{isAdmin ? 'No SOP cards yet — click "+ Add Card" to create one.' : 'No SOP cards yet.'}</p>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="sop-cards" direction="horizontal">
                {(provided) => (
                  <div
                    className="sop-cards-grid"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {cards.map((card, index) => (
                      <Draggable key={card.id} draggableId={String(card.id)} index={index}>
                        {(provided, snapshot) => (
                          <div
                            className={`sop-card${snapshot.isDragging ? ' dragging' : ''}`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <div className="sop-card-color-bar" style={{ background: card.color || '#2563eb' }} />
                            <div className="sop-card-inner">
                              <div className="sop-card-header">
                                <div className="sop-card-drag-handle" {...provided.dragHandleProps} title="Drag to reorder">⠿</div>
                                <h3 className="sop-card-title">{card.title}</h3>
                                {isAdmin && (
                                  <div className="sop-card-actions">
                                    <button className="sop-card-edit-btn" onClick={() => openEditCard(card)} title="Edit">✏️</button>
                                    <button className="sop-card-delete-btn" onClick={() => deleteCard(card.id)} title="Delete">✕</button>
                                  </div>
                                )}
                              </div>
                              <div
                                className="sop-card-body"
                                dangerouslySetInnerHTML={{ __html: card.body || '<em style="color:#94a3b8">No content</em>' }}
                              />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </section>

        {/* ── Process Flows ── */}
        <section className="pb-section">
          <div className="pb-section-title">
            <h2>Process Flows</h2>
            {isAdmin && !editing && (
              <button className="mtn-btn mtn-btn-edit" onClick={() => { setEditValue(content[activeTab]); setEditing(true); }}>Edit</button>
            )}
          </div>
          <div className="pb-tabs">
            <button className={activeTab === 'indeed' ? 'pb-tab active' : 'pb-tab'} onClick={() => { setActiveTab('indeed'); setEditing(false); }}>Indeed</button>
            <button className={activeTab === 'fadv'   ? 'pb-tab active' : 'pb-tab'} onClick={() => { setActiveTab('fadv');   setEditing(false); }}>First Advantage</button>
          </div>
          {editing ? (
            <div className="pb-editor">
              <textarea
                className="pb-textarea"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                placeholder={`Enter ${activeTab === 'indeed' ? 'Indeed' : 'First Advantage'} process flow…`}
              />
              <div className="pb-editor-actions">
                <button className={`mtn-btn mtn-btn-send${saving ? " mtn-btn-disabled" : ""}`} onClick={saveContent} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button className="mtn-btn mtn-btn-neutral" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            content[activeTab]
              ? <pre className="pb-pre">{content[activeTab]}</pre>
              : <p className="pb-empty">{isAdmin ? 'No content yet. Click Edit to add the process flow.' : 'No content added yet.'}</p>
          )}
        </section>

        {/* ── Task Tracker ── */}
        <section className="pb-section">
          <div className="pb-section-title">
            <h2>
              Task Tracker
              <span className="pb-week-label">Week of {weekLabel}</span>
              <span className="pb-week-label" style={{ marginLeft: 6 }}>{monthLabel}</span>
            </h2>
          </div>

          {liveCompanies.length === 0 ? (
            <p className="pb-empty">No Live accounts yet — add companies on the Accounts page first.</p>
          ) : (
            <div className="pb-matrix-row">
              {TYPES.map(type => renderTable(type))}
            </div>
          )}
        </section>

      </div>

      {/* ── Card create/edit modal ── */}
      {showCardModal && (
        <div className="sop-modal-overlay" onClick={e => { if (e.target === e.currentTarget && !savingCard) setShowCardModal(false); }}>
          <div className="sop-modal">
            <div className="sop-modal-header">
              <h3>{editingCard ? 'Edit Card' : 'New SOP Card'}</h3>
              <button className="sop-modal-close" onClick={() => setShowCardModal(false)}>×</button>
            </div>

            <div className="sop-modal-body">
              <div className="sop-form-field">
                <label>Heading</label>
                <input
                  className="sop-form-input"
                  value={cardForm.title}
                  onChange={e => setCardForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Card heading"
                  autoFocus
                />
              </div>

              <div className="sop-form-field">
                <label>Color</label>
                <div className="sop-color-picker">
                  {CARD_COLORS.map(c => (
                    <button
                      key={c.value}
                      className={`sop-color-swatch${cardForm.color === c.value ? ' selected' : ''}`}
                      style={{ background: c.value }}
                      title={c.label}
                      onClick={() => setCardForm(f => ({ ...f, color: c.value }))}
                    />
                  ))}
                </div>
              </div>

              <div className="sop-form-field">
                <label>Body</label>
                <div className="sop-editor-toolbar">
                  <button className="sop-toolbar-btn" onMouseDown={e => { e.preventDefault(); execBold(); }} title="Bold"><strong>B</strong></button>
                  <button className="sop-toolbar-btn" onMouseDown={e => { e.preventDefault(); execBullet(); }} title="Bullet list">• List</button>
                </div>
                <div
                  ref={editorRef}
                  className="sop-editor"
                  contentEditable
                  suppressContentEditableWarning
                  data-placeholder="Write content here…"
                />
              </div>
            </div>

            <div className="sop-modal-footer">
              <button className={`mtn-btn mtn-btn-neutral${savingCard ? " mtn-btn-disabled" : ""}`} onClick={() => setShowCardModal(false)} disabled={savingCard}>Cancel</button>
              <button className={`mtn-btn mtn-btn-send${savingCard ? " mtn-btn-disabled" : ""}`} onClick={saveCard} disabled={savingCard}>
                {savingCard ? 'Saving…' : editingCard ? 'Save Changes' : 'Create Card'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
